import { useMutation } from '@tanstack/react-query';

import type { UiVersion } from '@/types';
import type {
  AccessPassResponse,
  CancelClientOnlineOrderParams,
  CancelClientOnlineOrderResponse,
  CancelClientOrderParams,
  CancelClientOrderResponse,
  ClientFormResponse,
  EditPasswordFormData,
  EditPasswordResponse,
  MutationResponse,
  SignUpFormData,
  SignUpResponse,
} from '@/types/api/mutations';

import { HttpClient } from '@/network/HttpClient';

export const useClientFormMutation = () =>
  useMutation({
    mutationFn: (params: {
      url: string;
      method: 'POST' | 'PUT';
      data:
        | FormData
        | { client: Record<string, unknown>; custom_field_values?: unknown[] };
    }) => {
      if (params.data instanceof FormData) {
        return HttpClient.request<ClientFormResponse>({
          method: params.method,
          url: params.url,
          data: params.data,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      }

      return HttpClient.request<ClientFormResponse>({
        method: params.method,
        url: params.url,
        data: {
          client: params.data.client,
          ...(params.data.custom_field_values && {
            custom_field_values: params.data.custom_field_values,
          }),
        },
      });
    },
  });

export const useMergeClientsMutation = () =>
  useMutation({
    mutationFn: (params: { url: string; clientIdToMerge: number }) =>
      HttpClient.request({
        method: 'POST',
        url: params.url,
        data: { client_id_to_merge: params.clientIdToMerge },
      }),
  });

export const useClientSignUpMutation = () =>
  useMutation({
    mutationFn: (params: { url: string; data: SignUpFormData }) =>
      HttpClient.request<SignUpResponse>({
        method: 'POST',
        url: params.url,
        data: params.data,
      }),
  });

export const useCancelClientOrderMutation = () =>
  useMutation({
    mutationFn: (params: CancelClientOrderParams) =>
      HttpClient.request<CancelClientOrderResponse>({
        method: 'DELETE',
        url: `${params.url}/${params.id}`,
        data: {
          client_user_order: {
            to_deposit: params.toDeposit ?? false,
          },
        },
      }),
  });

export const useCancelClientOnlineOrderMutation = () =>
  useMutation({
    mutationFn: (params: CancelClientOnlineOrderParams) =>
      HttpClient.request<CancelClientOnlineOrderResponse>({
        method: 'DELETE',
        url: `${params.url}/${params.id}`,
      }),
  });

export const useClientEditPasswordMutation = () =>
  useMutation({
    mutationFn: (params: { url: string; data: EditPasswordFormData }) =>
      HttpClient.request<EditPasswordResponse>({
        method: 'PUT',
        url: params.url,
        data: params.data,
      }),
  });

export const useClientAppVersionMutation = () =>
  useMutation({
    mutationFn: (params: { url: string; version: UiVersion }) =>
      HttpClient.request<MutationResponse>({
        method: 'POST',
        url: params.url,
        data: { client: { client_app_version: params.version } },
      }),
  });

export const useCreateAccessPassMutation = () =>
  useMutation({
    mutationFn: (params: { url: string; purchased_service_id: number }) =>
      HttpClient.request<AccessPassResponse>({
        method: 'POST',
        url: params.url,
        data: { purchased_service_id: params.purchased_service_id },
      }),
  });
